import { Alert, Button, List, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import moment from 'moment';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { shallowEqual } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import routeConfiguration from '../../routeConfiguration';
import { getClientData, setClientData } from '../../util/clientData';
import { isTooManyEmailVerificationRequestsError } from '../../util/errors';
import useAppSelector from '../../util/hooks/useAppSelector';
import {
  STRIPE_ISSUE_BLOCKING,
  getStripeAccountIssue,
} from '../../util/stripeAccount';
import { FormattedMessage } from '../../util/reactIntl';
import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import Link from '../Links/Link/Link';
import Modal from '../ModalNew/Modal';

const MISSING_INFORMATION_MODAL_WHITELIST = [
  'SigninPage',
  'SignupPage',
  'ContactSettingsPage',
  'EmailVerificationPage',
  'PasswordResetPage',
  'StripePayoutPage',
];

const EMAIL_VERIFICATION = 'EMAIL_VERIFICATION';
const STRIPE_ACCOUNT = 'STRIPE_ACCOUNT';
const STRIPE_VERIFICATION_PENDING = 'STRIPE_VERIFICATION_PENDING';
// Der Account kann keine Überweisung annehmen: Buchungen auf die Inserate
// dieses Gastgebers werden serverseitig abgelehnt (initiate-privileged). Das
// ist etwas anderes als "Stripe hätte gern noch Unterlagen" und muss auch
// anders klingen.
const STRIPE_ACCOUNT_BOOKINGS_BLOCKED = 'STRIPE_ACCOUNT_BOOKINGS_BLOCKED';

const LOCAL_STORAGE_KEY = 'vansite.lastClosedMissingInfoModal';

// Wie lange das Popup nach dem Schließen unterdrückt wird. Beim blockierten
// Account bleibt es bei den kurzen zwei Stunden - da hängt jede Buchung dran.
// Wenn Stripe dagegen noch prüft, kann der Gastgeber nichts tun; ihn dann alle
// zwei Stunden daran zu erinnern ist nur lästig.
const SNOOZE_HOURS_DEFAULT = 2;
const SNOOZE_HOURS_WAITING_ON_STRIPE = 24;

const requirementItemSx = { py: 0.25 };

/**
 * @param {object} props Component props
 * @param {object} [props.sendVerificationEmailError] Send verification email error
 * @param {boolean} [props.sendVerificationEmailInProgress] Send verification email in progress
 * @param {Function} props.onResendVerificationEmail On resend verification email
 * @param {Function} props.onReFetchCurrentUser On re fetch current user
 * @returns {React.ReactNode}
 */
const MissingInformationModalComponent = ({
  sendVerificationEmailError = null,
  sendVerificationEmailInProgress = false,
  onResendVerificationEmail,
  onReFetchCurrentUser,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();

  const { currentUser, currentUserHasListings, currentUserHasOrders } = useAppSelector(
    (state) => ({
      currentUser: state.user.currentUser,
      currentUserHasListings: state.user.currentUserHasListings,
      currentUserHasOrders: state.user.currentUserHasOrders,
    }),
    shallowEqual,
  );

  const connectedStripeAccount = useAppSelector((state) => state.stripeConnectAccount.stripeAccount);

  const [showMissingInformationReminder, setShowMissingInformationReminder] = useState(null);
  const [hasSeenMissingInformationReminder, setHasSeenMissingInformationReminder] = useState(false);

  const stripeAccountData = connectedStripeAccount?.attributes.stripeAccountData;
  const stripeIssue = getStripeAccountIssue(stripeAccountData);

  const goToPayoutSettings = useCallback(
    () =>
      navigate(
        createResourceLocatorString('StripePayoutPage', routeConfiguration(), {}, {}, intl.locale),
      ),
    [navigate, intl.locale],
  );

  let content = null;
  let title = null;
  let action = null;
  const currentUserLoaded = currentUser && currentUser.id;
  if (currentUserLoaded) {
    if (showMissingInformationReminder === EMAIL_VERIFICATION) {
      const email = currentUser.id ? (
        <Typography component="span" sx={{ color: 'info.main' }}>
          {currentUser.attributes.email}
        </Typography>
      ) : (
        ''
      );

      const resendEmailLink = (
        <Link onClick={(e) => { e.preventDefault(); onResendVerificationEmail(); }} to="/">
          <FormattedMessage id="ModalMissingInformation.resendEmailLinkText" />
        </Link>
      );

      const fixEmailLink = (
        <Link to={{ name: 'ContactSettingsPage' }}>
          <FormattedMessage id="ModalMissingInformation.fixEmailLinkText" />
        </Link>
      );

      const resendErrorTranslationId = isTooManyEmailVerificationRequestsError(
        sendVerificationEmailError,
      )
        ? 'ModalMissingInformation.resendFailedTooManyRequests'
        : 'ModalMissingInformation.resendFailed';
      const resendErrorMessage = sendVerificationEmailError ? (
        <Alert security="error">
          <FormattedMessage id={resendErrorTranslationId} />
        </Alert>
      ) : null;

      title = intl.formatMessage({ id: 'ModalMissingInformation.verifyEmailTitle' });
      content = (
        <Stack spacing={2}>
          <Typography>
            <FormattedMessage id="ModalMissingInformation.verifyEmailText" />
          </Typography>
          <Typography>
            <FormattedMessage id="ModalMissingInformation.checkInbox" values={{ email }} />
          </Typography>
          {resendErrorMessage}
          <Typography>
            {sendVerificationEmailInProgress ? (
              <FormattedMessage id="ModalMissingInformation.sendingEmail" />
            ) : (
              <FormattedMessage
                id="ModalMissingInformation.resendEmail"
                values={{ resendEmailLink }}
              />
            )}
          </Typography>
          <Typography variant="caption">
            <FormattedMessage id="ModalMissingInformation.fixEmail" values={{ fixEmailLink }} />
          </Typography>
        </Stack>
      );
    } else if (showMissingInformationReminder === STRIPE_ACCOUNT) {
      title = intl.formatMessage({ id: 'ModalMissingInformation.missingStripeAccountTitle' });
      content = (
        <Typography>
          <FormattedMessage id="ModalMissingInformation.missingStripeAccountText" />
        </Typography>
      );
      action = (
        <Button
          color="primary"
          onClick={() =>
            navigate(
              createResourceLocatorString(
                'StripePayoutPage',
                routeConfiguration(),
                {},
                {},
                intl.locale,
              ),
            )
          }
          variant="contained"
        >
          <FormattedMessage id="ModalMissingInformation.gotoPaymentSettings" />
        </Button>
      );
    } else if (showMissingInformationReminder === STRIPE_VERIFICATION_PENDING) {
      title = intl.formatMessage({ id: 'ModalMissingInformation.verificationPendingTitle' });
      content = (
        <Typography>
          <FormattedMessage id="ModalMissingInformation.verificationPendingText" />
        </Typography>
      );
      action = (
        <Button color="primary" onClick={goToPayoutSettings} variant="contained">
          <FormattedMessage id="ModalMissingInformation.gotoPaymentSettings" />
        </Button>
      );
    } else if (showMissingInformationReminder === STRIPE_ACCOUNT_BOOKINGS_BLOCKED) {
      // stripeIssue is recomputed on every render, while showMissingInformationReminder
      // is state that this component only ever sets and never clears. As soon as the
      // account issue is resolved — or stripeAccountData is briefly absent during a
      // refetch — getStripeAccountIssue returns null while this branch is still the
      // selected one. Reading it unguarded took the whole page down via the error
      // boundary; an empty list simply omits the requirement section.
      const requirementCategories = stripeIssue?.requirementCategories ?? [];
      title = intl.formatMessage({ id: 'ModalMissingInformation.bookingsBlockedTitle' });
      content = (
        <Stack spacing={2}>
          <Alert severity="warning">
            <FormattedMessage id="ModalMissingInformation.bookingsBlockedWarning" />
          </Alert>
          <Typography>
            <FormattedMessage id="ModalMissingInformation.bookingsBlockedText" />
          </Typography>
          {requirementCategories.length > 0 ? (
            <>
              <Typography variant="subtitle2">
                <FormattedMessage id="ModalMissingInformation.missingRequirementsHeading" />
              </Typography>
              <List dense disablePadding>
                {requirementCategories.map((category) => (
                  <ListItem disableGutters key={category} sx={requirementItemSx}>
                    <ListItemText
                      primary={intl.formatMessage({
                        id: `ModalMissingInformation.requirement.${category}`,
                      })}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          ) : null}
        </Stack>
      );
      action = (
        <Button color="primary" onClick={goToPayoutSettings} variant="contained">
          <FormattedMessage id="ModalMissingInformation.gotoPaymentSettings" />
        </Button>
      );
    }
  }

  const handleMissingInformationReminder = useCallback(
    (newLocation) => {
      const routes = routeConfiguration();
      const whitelistedPaths = MISSING_INFORMATION_MODAL_WHITELIST.map((page) => {
        return pathByRouteName(page, routes, {}, intl.locale);
      });

      // Is the current page whitelisted?
      const isPageWhitelisted =
        whitelistedPaths.includes(newLocation.pathname) ||
        newLocation.pathname.includes('checkout');

      // Track if path changes inside Page level component
      const pathChanged = newLocation.pathname !== location.pathname;
      const notRemindedYet = !showMissingInformationReminder && !hasSeenMissingInformationReminder;

      // Is the reminder already shown on current page
      const showOnPathChange = notRemindedYet || pathChanged;

      if (!isPageWhitelisted && showOnPathChange) {
        const lastClosed = getClientData(LOCAL_STORAGE_KEY);
        const shouldSuppress = lastClosed === true;
        if (shouldSuppress) {
          return;
        }

        // Emails are sent when order is initiated
        // Customer is likely to get email soon when she books something
        // Provider email should work - she should get an email when someone books a listing
        const hasOrders = currentUserHasOrders === true;
        const hasListingsOrOrders = currentUserHasListings || hasOrders;

        const emailUnverified = !!currentUser?.id && !currentUser.attributes.emailVerified;
        const emailVerificationNeeded = hasListingsOrOrders && emailUnverified;

        const stripeAccountMissing = !!currentUser?.id && !currentUser.attributes.stripeConnected;
        const stripeAccountNeeded = currentUserHasListings && stripeAccountMissing;

        // Show reminder
        if (emailVerificationNeeded) {
          setShowMissingInformationReminder(EMAIL_VERIFICATION);
        } else if (stripeAccountNeeded) {
          setShowMissingInformationReminder(STRIPE_ACCOUNT);
        } else if (stripeIssue?.severity === STRIPE_ISSUE_BLOCKING) {
          // Stripe wartet auf den Gastgeber. Solange die transfers-Capability
          // fehlt, wird jede Buchung abgelehnt - das gehört deutlich gesagt.
          setShowMissingInformationReminder(STRIPE_ACCOUNT_BOOKINGS_BLOCKED);
        } else if (stripeIssue) {
          // Stripe prüft noch; er kann nichts nachreichen, nur warten.
          setShowMissingInformationReminder(STRIPE_VERIFICATION_PENDING);
        }
      }
    },
    [intl.locale, location, stripeIssue],
  );

  useEffect(() => {
    handleMissingInformationReminder(location);
  }, [handleMissingInformationReminder, location]);

  return (
    <Modal
      actions={[action]}
      onClose={() => {
        const snoozeHours =
          showMissingInformationReminder === STRIPE_VERIFICATION_PENDING
            ? SNOOZE_HOURS_WAITING_ON_STRIPE
            : SNOOZE_HOURS_DEFAULT;
        setClientData(LOCAL_STORAGE_KEY, true, moment().add(snoozeHours, 'hour'));
        setHasSeenMissingInformationReminder(true);
        setShowMissingInformationReminder(null);
        onReFetchCurrentUser();
      }}
      open={!!showMissingInformationReminder}
      title={title}
    >
      {content}
    </Modal>
  );
};

MissingInformationModalComponent.displayName = 'ModalMissingInformation';
const MissingInformationModal = memo(MissingInformationModalComponent);

export default MissingInformationModal;